import { useQuery } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import { CircleDollarSign, CircleOff, Image, Tags } from 'lucide-react'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'

const genderLabels: Record<string, string> = {
  '0': 'Nam',
  '1': 'Nữ',
  '2': 'Unisex',
}

const typeLabels: Record<string, string> = {
  money: 'Tiền mặt',
  black_money: 'Tiền bẩn',
  coin: 'Coin',
  ticket: 'Ticket',
  hidden: 'Ẩn',
}

function titleCase(value: string) {
  return value
    .split('_')
    .join(' ')
    .replace(/\b\w/g, (letter: string) => letter.toUpperCase())
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
        <CardTitle className='text-sm font-medium'>{label}</CardTitle>
        <Icon className='size-4 text-muted-foreground' />
      </CardHeader>
      <CardContent>
        <div className='text-2xl font-bold'>{value}</div>
      </CardContent>
    </Card>
  )
}

function Breakdown({
  data,
  labelFor,
  title,
}: {
  data: Record<string, number>
  labelFor: (key: string) => string
  title: string
}) {
  const entries = Object.entries(data).sort(
    ([, left], [, right]) => right - left
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className='grid gap-3'>
        {entries.length === 0 ? (
          <p className='text-sm text-muted-foreground'>Chưa có dữ liệu</p>
        ) : (
          entries.map(([key, count]) => (
            <div
              className='flex items-center justify-between gap-4 border-b pb-3 last:border-0 last:pb-0'
              key={key}
            >
              <span className='text-sm text-muted-foreground'>
                {labelFor(key)}
              </span>
              <span className='font-mono text-sm'>
                {count.toLocaleString('vi-VN')}
              </span>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const overview = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: api.statsOverview,
  })

  const data = overview.data
  const pricedPercent =
    data && data.total > 0 ? Math.round((data.priced / data.total) * 100) : 0

  return (
    <>
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className='mb-6 space-y-1'>
          <h1 className='text-2xl font-bold tracking-tight'>Bảng điều khiển</h1>
          <p className='text-muted-foreground'>
            Mức phủ giá trên thư viện ảnh quần áo.
          </p>
        </div>

        {overview.isPending ? (
          <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
            {Array.from({ length: 4 }, (_, index) => (
              <div
                className='h-28 animate-pulse rounded-xl bg-muted'
                key={index}
              />
            ))}
          </div>
        ) : overview.error ? (
          <div
            className='rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive'
            role='alert'
          >
            Không tải được bảng điều khiển: {overview.error.message}
          </div>
        ) : data ? (
          <>
            <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
              <MetricCard
                icon={Image}
                label='Tổng asset'
                value={data.total.toLocaleString('vi-VN')}
              />
              <MetricCard
                icon={CircleDollarSign}
                label='Đã có giá'
                value={data.priced.toLocaleString('vi-VN')}
              />
              <MetricCard
                icon={CircleOff}
                label='Chưa có giá'
                value={data.unpriced.toLocaleString('vi-VN')}
              />
              <MetricCard
                icon={Tags}
                label='Độ phủ giá'
                value={`${pricedPercent}%`}
              />
            </div>

            <div className='mt-4 grid gap-4 lg:grid-cols-3'>
              <Breakdown
                data={data.byGender}
                labelFor={(key) => genderLabels[key] ?? `Giới tính ${key}`}
                title='Theo giới tính'
              />
              <Breakdown
                data={data.byType}
                labelFor={(key) => typeLabels[key] ?? titleCase(key)}
                title='Theo loại giá'
              />
              <Breakdown
                data={data.byComponentType}
                labelFor={titleCase}
                title='Theo loại asset'
              />
            </div>
          </>
        ) : null}
      </Main>
    </>
  )
}
